import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useStores } from 'doc-mate-store/lib/hooks';
import { LoadStopWithFlag } from 'doc-mate-store/lib/models/LoadStop';
import { OrderLeg } from 'doc-mate-store/lib/models/OrderLeg';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React from 'react';
import LoadStatusTimelineItem from './LoadStatusTimelineItem';

type Props = {
  load: OrderLeg;
  otherLoads: OrderLeg[];
  legStops: LoadStopWithFlag[];
};

const LoadStatusTimeline: React.FC<Props> = ({
  load,
  otherLoads,
  legStops,
}) => {
  const classes = useStyles();
  const { rootStore } = useStores();
  const { me } = rootStore;
  const user = me ? me.maybeCurrent : undefined;
  const isDispatcher = user && user.isDispatcher;
  const isBroker = user && user.isBroker;

  const currentTrackingLoad = otherLoads.find(a => a.isTracking);

  const driverPosition = currentTrackingLoad?.currentGPSPosition;
  // const driverPosition = load.currentGPSPosition;
  const isCustomer = !(isBroker || isDispatcher);
  const now = moment();
  const timestamp =
    driverPosition &&
    driverPosition.timestamp &&
    moment(driverPosition.timestamp);
  const timestampDiff = now.diff(timestamp, 'minutes');

  // const hasDropoffETA =
  //   load.etas && load.etas.dropoffEta && load.etas.dropoffEta.eta;

  return (
    <div className={classes.container}>
      {!isCustomer && (
        <LoadStatusTimelineItem
          date={load.loadStartedOn}
          isNextItemActive={!!load.pickupStartedOn}
          statusText="Accepted"
          hidden={!load.loadStartedOn}
        />
      )}
      {legStops.map((stop, index) => {
        const { loadStop, isPickupDropff } = stop;
        const isLastStop = index === legStops.length - 1;

        const hasPickup = !!loadStop.pickupWindow?.lower;
        const hasDropoff = !!loadStop.dropoffWindow?.lower;
        const relatedLeg = otherLoads?.find(
          l =>
            l.pickup.current.location.id === loadStop.location.id ||
            l.dropoff.current.location.id === loadStop.location.id,
        );

        if (!relatedLeg) return null;

        console.log(loadStop, isPickupDropff);

        const noUpdateTooltip =
          "ETA Might change as driver's location is currently unavailable";

        // const hasPickupETA =
        //   load.etas && load.etas.pickupEta && load.etas.pickupEta.eta;
        const isPickupETAHidden =
          !relatedLeg.pickupStartedOn && !relatedLeg.pickupArrivedOn;
        const pickupHiddenTooltip = isPickupETAHidden
          ? 'The ETA will be updated once driver is in route to pick-up.'
          : !load.pickupArrivedOn &&
            (timestampDiff > 20 ||
              !driverPosition ||
              !(load.etas && load.etas.pickupEta))
          ? noUpdateTooltip
          : '';
        const isDropoffETAHidden =
          !relatedLeg.dropoffStartedOn && !relatedLeg.dropoffArrivedOn;
        const dropoffHiddenTooltip = isDropoffETAHidden
          ? 'The ETA will be updated once driver is in route to drop-off.'
          : !relatedLeg.dropoffArrivedOn &&
            (timestampDiff > 20 ||
              !driverPosition ||
              !(relatedLeg.etas && relatedLeg.etas.dropoffEta))
          ? noUpdateTooltip
          : '';
        return (
          <React.Fragment key={`${loadStop.location.id}-${index}`}>
            {hasPickup && (!isPickupDropff || relatedLeg.legNumber !== 1) && (
              <>
                <LoadStatusTimelineItem
                  prevDate={relatedLeg.loadStartedOn}
                  date={relatedLeg.pickupStartedOn}
                  isNextItemActive={!!relatedLeg.pickupArrivedOn}
                  statusText="En-Route to Pick-up"
                  // If it's hybrid, we might hide 'En-Route' since they are already there
                  hidden={isPickupDropff || !relatedLeg.pickupStartedOn}
                />
                <LoadStatusTimelineItem
                  onTimeStatus={
                    isCustomer ? undefined : relatedLeg.pickupOnTimeChipStatus
                  }
                  prevDate={relatedLeg.loadStartedOn}
                  window={relatedLeg.pickupWindow}
                  hidden={isPickupETAHidden}
                  hiddenTooltip={pickupHiddenTooltip}
                  date={relatedLeg.pickupArrivedOn}
                  etaDate={relatedLeg.etas?.pickupEta?.eta}
                  isNextItemActive={!!relatedLeg.dropoffStartedOn}
                  statusText="Arrived at Pick-up"
                  extraRender={(active, textClassName) => (
                    <>
                      <Typography className={textClassName}>
                        {loadStop.location.maybeCurrent?.name}
                      </Typography>
                      <Typography className={textClassName}>
                        {loadStop.city}, {loadStop.state} {loadStop.zipCode}
                      </Typography>
                    </>
                  )}
                />
              </>
            )}

            {hasDropoff && (!isPickupDropff || relatedLeg.legNumber === 1) && (
              <>
                <LoadStatusTimelineItem
                  date={relatedLeg.dropoffStartedOn}
                  isNextItemActive={!!relatedLeg.dropoffArrivedOn}
                  statusText="En-Route to Drop-off"
                  hidden={isDropoffETAHidden}
                  prevDate={relatedLeg.loadStartedOn}
                />
                <LoadStatusTimelineItem
                  date={relatedLeg.dropoffArrivedOn}
                  etaDate={relatedLeg.etas?.dropoffEta?.eta}
                  // If there's a pickup coming next at this same stop, the next item is that pickup
                  isNextItemActive={
                    hasPickup
                      ? !!relatedLeg.pickupStartedOn
                      : !!relatedLeg.dropoffCompletedOn
                  }
                  hidden={isDropoffETAHidden}
                  hiddenTooltip={dropoffHiddenTooltip}
                  onTimeStatus={
                    isCustomer ? undefined : relatedLeg.dropoffOnTimeChipStatus
                  }
                  statusText="Arrived at Drop-off"
                  window={relatedLeg.dropoffWindow}
                  extraRender={(active, textClassName) => (
                    <>
                      <Typography className={textClassName}>
                        {loadStop.location.maybeCurrent?.name}
                      </Typography>
                      <Typography className={textClassName}>
                        {loadStop.city}, {loadStop.state} {loadStop.zipCode}
                      </Typography>
                    </>
                  )}
                />
              </>
            )}

            {isLastStop && (
              <LoadStatusTimelineItem
                date={relatedLeg.dropoffCompletedOn}
                isLastItem={true}
                statusText="Delivered"
                hidden={!relatedLeg.dropoffCompletedOn}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: 24,
    paddingRight: 20,
    paddingBottom: 24,
    paddingLeft: 20,
  },
}));

export default observer(LoadStatusTimeline);
